import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare, ChevronDown, Check, Plus, Search,
  ShieldCheck, Smartphone, Sparkles, RefreshCw, Layers
} from "lucide-react";
import { useWhatsAppAccount } from "../../context/WhatsAppAccountContext";

export const WhatsAppAccountSwitcher = ({ variant = "navbar", collapsed = false, className = "" }) => {
  const {
    accounts,
    selectedAccount,
    selectedAccountId,
    selectAccount,
    refreshAccounts,
    loading,
  } = useWhatsAppAccount();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return accounts;
    const q = searchQuery.toLowerCase();
    return accounts.filter(
      (a) =>
        (a.verified_name && a.verified_name.toLowerCase().includes(q)) ||
        (a.phone_number && a.phone_number.includes(q)) ||
        (a.display_phone_number && a.display_phone_number.includes(q))
    );
  }, [accounts, searchQuery]);

  const handleSelect = (id) => {
    selectAccount(id);
    setIsOpen(false);
  };

  // Helper to format quality badge
  const getQualityBadge = (rating) => {
    const r = (rating || "").toUpperCase();
    if (r === "GREEN" || r === "HIGH") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/70 border border-emerald-200/60 dark:border-emerald-800/50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          High
        </span>
      );
    }
    if (r === "YELLOW" || r === "MEDIUM") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-950/70 border border-amber-200/60 dark:border-amber-800/50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          Medium
        </span>
      );
    }
    return null;
  };

  /* ─────────────────────────────────────────────────────────────
     VARIANT: SIDEBAR (Collapsible or Full card at top of sidebar)
  ───────────────────────────────────────────────────────────── */
  if (variant === "sidebar") {
    if (collapsed) {
      return (
        <div className="relative mt-2 mb-0.5 flex justify-center" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen((v) => !v)}
            title={selectedAccount ? `${selectedAccount.verified_name || selectedAccount.phone_number}` : "All WhatsApp Accounts"}
            className={`group relative flex h-10 w-10 items-center justify-center rounded-xl transition-all ${
              selectedAccountId !== "all"
                ? "bg-emerald-600 text-white shadow-md shadow-emerald-900/30"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            <MessageSquare className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900">
              <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping" />
            </span>
          </button>

          {isOpen && (
            <div className="absolute left-full top-0 ml-3 w-72 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 shadow-2xl z-50 animate-fade-in">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800 px-2">
                <span className="text-xs font-bold text-slate-800 dark:text-slate-100">WhatsApp Account</span>
                <button onClick={refreshAccounts} className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400">
                  <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                </button>
              </div>

              <div className="max-h-60 overflow-y-auto space-y-1 py-1.5">
                <button
                  onClick={() => handleSelect("all")}
                  className={`flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left text-xs transition-colors ${
                    selectedAccountId === "all"
                      ? "bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-700 dark:text-emerald-300"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-emerald-600" />
                    <span>All Accounts ({accounts.length})</span>
                  </div>
                  {selectedAccountId === "all" && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                </button>

                {accounts.map((acc) => (
                  <button
                    key={acc.id}
                    onClick={() => handleSelect(acc.id)}
                    className={`flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left text-xs transition-colors ${
                      String(selectedAccountId) === String(acc.id)
                        ? "bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-700 dark:text-emerald-300"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className="min-w-0 pr-2">
                      <p className="truncate font-semibold text-slate-900 dark:text-white">{acc.verified_name || "WhatsApp Business"}</p>
                      <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{acc.display_phone_number || acc.phone_number}</p>
                    </div>
                    {String(selectedAccountId) === String(acc.id) && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`relative mx-2.5 mt-2 mb-0.5 ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen((v) => !v)}
          className="group flex w-full items-center justify-between rounded-2xl border border-slate-200/80 dark:border-slate-800/90 bg-slate-50/90 dark:bg-slate-800/60 p-2.5 text-left transition-all hover:bg-white dark:hover:bg-slate-800 hover:border-emerald-500/50 hover:shadow-sm"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm shadow-emerald-900/30">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-xs font-bold text-slate-900 dark:text-white">
                  {selectedAccount ? (selectedAccount.verified_name || selectedAccount.phone_number) : "All WhatsApp Accounts"}
                </p>
              </div>
              <p className="truncate text-[11px] font-medium text-slate-500 dark:text-slate-400">
                {selectedAccount ? (selectedAccount.display_phone_number || selectedAccount.phone_number) : `${accounts.length} Active Channels`}
              </p>
            </div>
          </div>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180 text-emerald-500" : ""}`} />
        </button>

        {isOpen && (
          <div className="absolute left-0 top-full mt-2 w-full min-w-[260px] rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2 shadow-2xl z-50 animate-fade-in">
            {/* Search filter if more than 3 accounts */}
            {accounts.length > 3 && (
              <div className="relative mb-2 px-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 pl-8 pr-3 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            )}

            <div className="max-h-56 overflow-y-auto space-y-1">
              {/* Option: All Accounts */}
              <button
                onClick={() => handleSelect("all")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                  selectedAccountId === "all"
                    ? "bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-700 dark:text-emerald-300"
                    : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="font-bold">All WhatsApp Accounts</p>
                    <p className="text-[10px] text-slate-400">View combined data ({accounts.length})</p>
                  </div>
                </div>
                {selectedAccountId === "all" && <Check className="h-3.5 w-3.5 text-emerald-600" />}
              </button>

              <div className="my-1 border-t border-slate-100 dark:border-slate-800" />

              {/* Accounts list */}
              {filteredAccounts.length === 0 ? (
                <p className="py-2 text-center text-xs text-slate-400">No accounts found</p>
              ) : (
                filteredAccounts.map((acc) => {
                  const isSelected = String(selectedAccountId) === String(acc.id);
                  return (
                    <button
                      key={acc.id}
                      onClick={() => handleSelect(acc.id)}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                        isSelected
                          ? "bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-700 dark:text-emerald-300"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate font-semibold text-slate-900 dark:text-white">
                            {acc.verified_name || "WhatsApp Business"}
                          </p>
                          {getQualityBadge(acc.quality_rating)}
                        </div>
                        <p className="truncate text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                          {acc.display_phone_number || acc.phone_number}
                        </p>
                      </div>
                      {isSelected && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
                    </button>
                  );
                })
              )}
            </div>

            {/* Quick Action: Add Account */}
            <div className="mt-2 border-t border-slate-100 dark:border-slate-800 pt-2">
              <Link
                to="/account?add=true"
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 py-1.5 text-xs font-bold text-white shadow-sm transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add WhatsApp Account
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     VARIANT: NAVBAR (Pill dropdown placed in the top header)
  ───────────────────────────────────────────────────────────── */
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-2 rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-800/80 px-3 py-1.5 text-left text-xs transition-all hover:bg-white dark:hover:bg-slate-800 hover:border-emerald-500/50 shadow-sm"
        title="Filter by WhatsApp Account"
      >
        <div className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white shadow-sm">
          <MessageSquare className="h-3.5 w-3.5" />
          <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
            <span className="h-full w-full rounded-full bg-emerald-400 animate-ping opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
        </div>

        <div className="hidden sm:flex flex-col min-w-0 max-w-[170px] md:max-w-[220px]">
          <span className="truncate text-[11px] font-bold text-slate-800 dark:text-slate-100 leading-tight">
            {selectedAccount ? (selectedAccount.verified_name || selectedAccount.phone_number) : "All WhatsApp Accounts"}
          </span>
          <span className="truncate text-[10px] text-slate-500 dark:text-slate-400 font-mono leading-tight">
            {selectedAccount ? (selectedAccount.display_phone_number || selectedAccount.phone_number) : `${accounts.length} Channel${accounts.length !== 1 ? 's' : ''}`}
          </span>
        </div>

        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-180 text-emerald-500" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 sm:left-0 top-full mt-2 w-72 sm:w-80 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 shadow-2xl z-50 animate-fade-in">
          <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800 px-1">
            <div>
              <p className="text-xs font-bold text-slate-800 dark:text-slate-100">WhatsApp Accounts</p>
              <p className="text-[10px] text-slate-400">Filter platform data by account</p>
            </div>
            <button
              onClick={refreshAccounts}
              title="Refresh accounts"
              className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* Search filter if accounts > 3 */}
          {accounts.length > 3 && (
            <div className="relative my-2 px-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search account by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 pl-8 pr-3 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          )}

          <div className="max-h-60 overflow-y-auto space-y-1 py-1">
            {/* Option: All Accounts */}
            <button
              onClick={() => handleSelect("all")}
              className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                selectedAccountId === "all"
                  ? "bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-700 dark:text-emerald-300"
                  : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400">
                  <Layers className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-white">All WhatsApp Accounts</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Combined data across all {accounts.length} channels</p>
                </div>
              </div>
              {selectedAccountId === "all" && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
            </button>

            <div className="my-1 border-t border-slate-100 dark:border-slate-800" />

            {/* List of Accounts */}
            {filteredAccounts.length === 0 ? (
              <div className="py-4 text-center text-xs text-slate-400">
                {accounts.length === 0 ? "No WhatsApp accounts connected yet." : "No accounts match your search."}
              </div>
            ) : (
              filteredAccounts.map((acc) => {
                const isSelected = String(selectedAccountId) === String(acc.id);
                return (
                  <button
                    key={acc.id}
                    onClick={() => handleSelect(acc.id)}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs transition-colors ${
                      isSelected
                        ? "bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-700 dark:text-emerald-300"
                        : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        <Smartphone className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate font-bold text-slate-900 dark:text-white">
                            {acc.verified_name || "WhatsApp Business"}
                          </p>
                          {getQualityBadge(acc.quality_rating)}
                        </div>
                        <p className="truncate text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                          {acc.display_phone_number || acc.phone_number}
                        </p>
                      </div>
                    </div>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Add Account CTA */}
          <div className="mt-2 border-t border-slate-100 dark:border-slate-800 pt-2">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate("/account?add=true");
              }}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 py-2 text-xs font-bold text-white shadow-sm transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add WhatsApp Account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppAccountSwitcher;
